import React, { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useUpdateEffect } from 'react-use';
import useCarouselSettings from '@drc/components/SharedCarousel/hooks';
import { Box, Heading, useBreakpointValue, useInView } from '@stockx/ui';
import { useTranslation } from 'react-i18next';
import { Categories, Events, useGATrackEvent } from '@app/analytics';
import { trackEvent as segmentTrackEvent, EVENTS, SCREENS } from '@app/segment';
import { StyledUserGeneratedContentCarousel } from './UserGeneratedContentCarousel.styles';
import { useUserGeneratedContentCollection } from './hooks';
import { UserGeneratedContentTile } from './components/UserGeneratedContentTile';
import { Loading } from './Loading';
import entrieState from './states/entrieState';

interface UserGeneratedContentCarouselProps {
  collection: string;
}

const UserGeneratedContentCarousel: React.FC<
  UserGeneratedContentCarouselProps
> = ({ collection = '' }) => {
  const { data, loading, error } = useUserGeneratedContentCollection({
    collection,
  });

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '0px 0px 42px 0px',
  });

  const gaTrack = useGATrackEvent();

  // Impression Event Tracking
  useEffect(() => {
    if (!inView) {
      return;
    }

    gaTrack(Events.AsSeenOnInstagramImpression, Categories.Home);
    segmentTrackEvent(EVENTS.AS_SEEN_ON_INSTAGRAM_IMPRESSION, {
      screenName: SCREENS.HOME,
    });
  }, [inView]);

  const [entries, setEntries] = useRecoilState(entrieState);

  useUpdateEffect(() => {
    if (!entries?.length) {
      setEntries(data?.entries);
    }
  }, [data?.entries]);

  const { isMobile } = useBreakpointValue();
  const MIN_LENGTH = isMobile ? 2 : 4;

  const { t } = useTranslation();
  const settings = useCarouselSettings({
    isBigButtons: true,
    slidesToShow: 4.15,
    slidesToScroll: 4,
  });

  if (loading) {
    return <Loading />;
  }

  if (error || (data?.entries && data?.entries?.length < MIN_LENGTH)) {
    return null;
  }

  return (
    <Box data-component="UserGeneratedContentRow" ref={ref}>
      <Box mt={{ base: 2, md: 6 }}>
        <Heading size="lg" variant="medium">
          {t(data?.title)}
        </Heading>
        <Heading
          as="h3"
          size={{ base: 'xs', md: 'sm' }}
          mb={{ base: 2, md: 4 }}
        >
          {t(data?.subtitle)}
        </Heading>
      </Box>
      <StyledUserGeneratedContentCarousel {...settings}>
        {data?.entries?.map((tile, index) => (
          <UserGeneratedContentTile
            title={tile.title}
            creator={tile.creator}
            image={tile.image}
            idx={index}
            key={`ugc-tile-${index}`}
          />
        ))}
      </StyledUserGeneratedContentCarousel>
    </Box>
  );
};

export default UserGeneratedContentCarousel;
